import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { MoveProductsToPositionDocument } from '@/lib/graphql/generated/graphql';
import { showDomainErrorToast } from '@/lib/utils/domain-error-translator';
import { recentlyMovedGroupKeysState } from '@/states/recently-moved-group-keys.state';
import { useMutation } from '@apollo/client/react';
import { useSetAtom } from 'jotai';

import { useDealNotEditableHandler } from './useDealNotEditableHandler';

const HIGHLIGHT_DURATION_MS = 5_000;

export function useMoveProductsToPosition(dealId: string) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [mutate, { loading }] = useMutation(MoveProductsToPositionDocument);
  const setRecentlyMovedGroupKeys = useSetAtom(recentlyMovedGroupKeysState);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();

  const moveProductsToPosition = async (
    productGroupKeys: string[],
    targetPosition: number
  ) => {
    try {
      const result = await mutate({
        variables: { dealId, productGroupKeys, targetPosition },
      });

      const payload = result.data?.moveProductsToPosition;
      if (payload?.__typename === 'DomainException') {
        if (handleIfDealNotEditable(payload.code)) return result;
        showDomainErrorToast({
          toast,
          t,
          code: payload.code,
          titleKey: 'general.error',
          defaultMessageKey: 'general.error_business',
        });
        return result;
      }

      if (
        result?.data?.moveProductsToPosition?.__typename === 'MutationSuccess'
      ) {
        toast({
          title: t('general.success'),
          description: t('kpi.move_success'),
        });
      }

      setRecentlyMovedGroupKeys(new Set<string>(productGroupKeys));
      setTimeout(
        () => setRecentlyMovedGroupKeys(new Set<string>()),
        HIGHLIGHT_DURATION_MS + 100
      );

      return result;
    } catch (err) {
      console.error('[useMoveProductsToPosition]', err);
      toast({
        title: t('general.error'),
        description: t('general.error_business'),
        variant: 'destructive',
      });
      throw err;
    }
  };

  return { moveProductsToPosition, loading };
}
